import { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const FREQ = [
  { value: 12, label: 'per month' },
  { value: 1, label: 'per year' },
  { value: 52, label: 'per week' },
  { value: 365, label: 'per day' },
];

const DEFAULT_CATEGORIES = [
  { id: 'vehicle-fuel', name: 'Vehicle – Fuel & Mileage', amount: 0, freq: 12 },
  { id: 'vehicle-finance', name: 'Vehicle – Lease/Finance', amount: 0, freq: 12 },
  { id: 'vehicle-mot-tax', name: 'Vehicle – MOT, Road Tax & Servicing', amount: 0, freq: 1 },
  { id: 'insurance-public', name: 'Insurance – Public Liability', amount: 0, freq: 1 },
  { id: 'insurance-van', name: 'Insurance – Van', amount: 0, freq: 1 },
  { id: 'insurance-tools', name: 'Insurance – Tools', amount: 0, freq: 1 },
  { id: 'insurance-employer', name: "Insurance – Employer's Liability", amount: 0, freq: 1 },
  { id: 'tools-purchase', name: 'Tools – Purchase & Replacement', amount: 0, freq: 12 },
  { id: 'tools-hire', name: 'Tools – Plant Hire Budget', amount: 0, freq: 12 },
  { id: 'premises-rent', name: 'Premises – Yard/Unit Rent', amount: 0, freq: 12 },
  { id: 'premises-utilities', name: 'Premises – Utilities', amount: 0, freq: 12 },
  { id: 'admin-accountant', name: 'Admin – Accountant', amount: 0, freq: 1 },
  { id: 'admin-phone', name: 'Admin – Phone & Internet', amount: 0, freq: 12 },
  { id: 'admin-software', name: 'Admin – Software & Subscriptions', amount: 0, freq: 12 },
  { id: 'admin-advertising', name: 'Admin – Advertising & Marketing', amount: 0, freq: 12 },
  { id: 'pension', name: 'Pension Contributions', amount: 0, freq: 12 },
  { id: 'training', name: 'Training & Certifications', amount: 0, freq: 1 },
  { id: 'ppe-clothing', name: 'PPE & Workwear', amount: 0, freq: 1 },
  { id: 'wastage-buffer', name: 'Materials Wastage Buffer', amount: 0, freq: 12 },
];

const slug = () => Math.random().toString(36).slice(2, 9);
const gbp = (n) => `£${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function OverheadsCard() {
  const [cats, setCats] = useState(DEFAULT_CATEGORIES);
  const [customName, setCustomName] = useState('');
  const [billableHours, setBillableHours] = useState(30);
  const [billableWeeks, setBillableWeeks] = useState(48);

  const update = (id, k, v) => setCats((arr) => arr.map((c) => c.id === id ? { ...c, [k]: v } : c));
  const remove = (id) => setCats((arr) => arr.filter((c) => c.id !== id));
  const add = () => {
    if (!customName.trim()) return;
    setCats((arr) => [...arr, { id: slug(), name: customName.trim(), amount: 0, freq: 12, custom: true }]);
    setCustomName('');
  };
  const reset = () => setCats(DEFAULT_CATEGORIES);

  const { monthly, annual, perHour } = useMemo(() => {
    let annual = 0;
    cats.forEach((c) => {
      const amt = parseFloat(c.amount) || 0;
      const f = parseInt(c.freq) || 12;
      annual += amt * f;
    });
    const monthly = annual / 12;
    const hoursPerYear = (parseFloat(billableHours) || 0) * (parseFloat(billableWeeks) || 0);
    const perHour = hoursPerYear > 0 ? annual / hoursPerYear : 0;
    return { monthly, annual, perHour };
  }, [cats, billableHours, billableWeeks]);

  return (
    <article data-testid="calc-card-overheads" className="tm-card p-5 sm:p-6 flex flex-col gap-4 bg-white lg:col-span-2">
      <header>
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display font-bold text-lg sm:text-xl tracking-tight uppercase leading-tight">Business Overheads</h3>
          <span className="tm-chip tm-chip-orange shrink-0">overheads</span>
        </div>
        <p className="text-sm text-slate-600 mt-1">Add every running cost. We&apos;ll show your monthly &amp; annual overhead plus the hourly uplift to recover them on every billable hour.</p>
      </header>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-4">
          <div className="space-y-2">
            {cats.map((c) => (
              <div key={c.id} className="grid grid-cols-12 gap-2 items-center border border-slate-200 p-2 bg-white">
                <input
                  data-testid={`oh-name-${c.id}`}
                  value={c.name}
                  onChange={(e) => update(c.id, 'name', e.target.value)}
                  className="col-span-12 sm:col-span-5 px-2 py-2 border border-slate-200 font-mono text-sm min-h-[44px]"
                />
                <div className="col-span-5 sm:col-span-3 relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 font-mono text-sm text-slate-500">£</span>
                  <input
                    data-testid={`oh-amount-${c.id}`}
                    type="number" step="any" inputMode="decimal"
                    value={c.amount}
                    onChange={(e) => update(c.id, 'amount', e.target.value)}
                    className="w-full pl-6 pr-2 py-2 border border-slate-200 font-mono text-sm min-h-[44px]"
                    placeholder="0.00"
                  />
                </div>
                <select
                  data-testid={`oh-freq-${c.id}`}
                  value={c.freq}
                  onChange={(e) => update(c.id, 'freq', parseInt(e.target.value))}
                  className="col-span-5 sm:col-span-3 px-2 py-2 border border-slate-200 font-mono text-sm bg-white min-h-[44px]"
                >
                  {FREQ.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
                <button
                  data-testid={`oh-remove-${c.id}`}
                  onClick={() => remove(c.id)}
                  className="col-span-2 sm:col-span-1 border border-slate-200 bg-white hover:bg-red-50 p-2 min-h-[44px] inline-flex items-center justify-center"
                  aria-label="Remove"
                >
                  <Trash2 size={16}/>
                </button>
              </div>
            ))}
          </div>

          <div className="border-t border-dashed border-slate-200 pt-4">
            <p className="tm-label">Add a custom overhead</p>
            <div className="flex gap-2">
              <input
                data-testid="oh-custom-name"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                placeholder="e.g. Workshop heating, Apprentice wages…"
                className="tm-input flex-1"
              />
              <button data-testid="oh-custom-add" onClick={add} className="tm-btn">
                <Plus size={16}/> Add
              </button>
            </div>
            <button data-testid="oh-reset" onClick={reset} className="mt-2 text-xs uppercase tracking-widest text-slate-500 hover:text-brand font-semibold">Reset defaults</button>
          </div>

          <div className="border border-slate-200 p-4 bg-slate-50">
            <p className="tm-label">Billable capacity (for hourly uplift)</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-600 font-semibold">Hours / week</span>
                <input data-testid="oh-hours-week" type="number" step="any" value={billableHours} onChange={(e) => setBillableHours(e.target.value)} className="tm-input mt-1"/>
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-600 font-semibold">Weeks / year</span>
                <input data-testid="oh-weeks-year" type="number" step="any" value={billableWeeks} onChange={(e) => setBillableWeeks(e.target.value)} className="tm-input mt-1"/>
              </label>
            </div>
          </div>
        </div>

        <div className="tm-result h-fit">
          <p className="text-xs uppercase tracking-widest text-slate-300">Annual overhead</p>
          <p data-testid="oh-annual" className="font-mono font-semibold text-3xl text-brand mt-1">{gbp(annual)}</p>
          <hr className="my-3 border-slate-700"/>
          <Row k="Per month" v={gbp(monthly)}/>
          <Row k="Per week" v={gbp(annual/52)}/>
          <Row k="Per day (5)" v={gbp(annual/(52*5))}/>
          <hr className="my-3 border-slate-700"/>
          <p className="text-xs uppercase tracking-widest text-slate-300">Hourly uplift required</p>
          <p data-testid="oh-uplift" className="font-mono font-semibold text-2xl text-white mt-1">{gbp(perHour)}<span className="text-slate-400 text-xs">/hr</span></p>
          <p className="text-xs text-slate-400 mt-2">Add to every quoted hour to break even on overheads.</p>
        </div>
      </div>
    </article>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline justify-between py-1 font-mono text-sm">
      <span className="text-slate-300 uppercase text-xs tracking-widest">{k}</span>
      <span className="font-bold">{v}</span>
    </div>
  );
}
